import type { ApolloClient } from '@apollo/client'
import { WORKLOGS, WORKLOGS_AUTORIZATION } from '../gql/verteOfficeGQL'
import { computeDailyCaps } from './dailyHourCaps'
import { businessDateRangeToUtcDateTimeRange, toBusinessDate } from './businessDate'

export interface DailyCapViolation {
  messageKey: 'hh_validation_daily_cap_exceeded'
  params: { total: string; cap: number; date: string }
}

/**
 * Chequeo de tope diario para creaciones de worklog fuera del workbook
 * (formularios popup). Aplica la misma invariante que la regla de Guardar
 * (`validation/dailyCapRule.ts`): total del día <= tope de jornada autorizado
 * ?? 12, máx 24. Devuelve la violación o null si pasa.
 */
export async function checkDailyCapForNewWorklog (params: {
  client: ApolloClient<any>
  userId: string
  businessDate: string
  addedHours: number
}): Promise<DailyCapViolation | null> {
  const { client, userId, businessDate, addedHours } = params
  if (!userId || !businessDate || !(addedHours > 0)) return null

  const dateTimeRange = businessDateRangeToUtcDateTimeRange({ from: businessDate, to: businessDate })

  const [worklogsResult, authorizationsResult] = await Promise.all([
    client.query({
      query: WORKLOGS,
      variables: {
        where: {
          deleted_at: { equals: null },
          user_id: { equals: userId },
          status: { equals: 'COMPLETE' },
          start_time: dateTimeRange,
        },
      },
      fetchPolicy: 'network-only',
    }),
    client.query({
      query: WORKLOGS_AUTORIZATION,
      variables: {
        where: {
          deleted_at: { equals: null },
          user_id: { equals: userId },
          date: dateTimeRange,
        },
      },
      fetchPolicy: 'network-only',
    }),
  ])

  const worklogs: any[] = worklogsResult.data?.worklogs ?? []
  const authorizations: any[] = authorizationsResult.data?.worklogAuthorizations ?? []

  let total = addedHours
  for (const worklog of worklogs) {
    if (toBusinessDate(worklog.start_time) !== businessDate) continue
    total += Number(worklog.total_hours || 0)
  }

  // Si el día tiene varias filas con tope de jornada, gana el mayor.
  const dailyLimit = authorizations.reduce(
    (max, authorization) => Math.max(max, Number(authorization.daily_limit_hours || 0)),
    0
  )
  const caps = computeDailyCaps(dailyLimit)

  if (total > caps.totalCap + 0.000001) {
    return {
      messageKey: 'hh_validation_daily_cap_exceeded',
      params: { total: total.toFixed(2), cap: caps.totalCap, date: businessDate },
    }
  }
  return null
}
